import React from 'react';
import { IonPage, IonContent, useIonViewWillLeave } from '@ionic/react';
import { useParams } from 'react-router-dom';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { useTopicState } from '@/modules/topic/TopicStateContext';
import { selectTopicMutations } from '@/modules/topic/reducer';
import TopicChangeList from '@/modules/topic/TopicChangeList';
import { formatMutationTimestamp } from '@/modules/topic/mutations';
import { pxToRem } from '@/modules/config';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import ShareWizardHeader from './ShareWizardHeader';
import UpdateSummaryField from './UpdateSummaryField';

/**
 * Update-flow step (#427): re-presents the expandable change list since the
 * selected head as the page content, and collects the required summary of the
 * changes (the {_updateSummary} primitive) in a keyboard-tier FooterDock row
 * above the Next button — so focusing it rises the field above the on-screen
 * keyboard and hides Next until the user is done (the same pattern as the
 * compose field editor and the contact-search pill).
 */
const ChangesStep: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const { topic, aboutChangesSince, updateSummary, setUpdateSummary, nav } =
    useShareWizardContext();
  const { state } = useTopicState();

  // The shared change list runs from the head (the selected change) forward.
  // Compare parsed instants: mutation timestamps and the head may come from
  // different producers with different fractional-second formats.
  const headMs = aboutChangesSince ? Date.parse(aboutChangesSince) : NaN;
  const mutations = selectTopicMutations(state, topicId).filter(
    m => Number.isNaN(headMs) || Date.parse(m.at) >= headMs
  );

  const canAdvance = updateSummary.trim().length > 0;

  const blurActive = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  useIonViewWillLeave(() => {
    blurActive();
  });

  return (
    <IonPage>
      <ShareWizardHeader
        topicTitle={topic?.title ?? ''}
        title="Changes"
        progress={nav.progress}
        onBack={nav.goBack}
      />
      <IonContent style={{ '--background': 'var(--app-page-background)' }}>
        <div
          data-testid="changes-step-list"
          style={{
            // Bottom clearance so the last change scrolls clear of the resting
            // FooterDock (the summary card floats one gap above the Next row):
            // Next topic height + its safe-area clearance + gap + an estimate
            // of the ~4-row summary card. Tune the constant on device.
            padding:
              '0 0 calc(var(--footer-action-height) + var(--footer-dock-bottom) + var(--footer-dock-gap) + 190px)',
          }}
        >
          {aboutChangesSince && (
            <h2
              style={{
                fontSize: pxToRem(16),
                fontWeight: 600,
                margin: '16px 16px 4px',
              }}
              data-testid="changes-step-since"
            >
              Since {formatMutationTimestamp(aboutChangesSince)}
            </h2>
          )}
          <TopicChangeList
            mutations={mutations}
            emptyMessage="Change details aren't available on this device."
          />
        </div>
      </IonContent>
      <FooterDock>
        {/* Keyboard-tier row: the summary rises above the keyboard on focus and
            hides the Next row until the field blurs (footer.css handles both).
            paddingBottom: 0 because the row rests one gap above the Next row,
            which already carries the safe-area clearance. */}
        <FooterDockRow pin="keyboard" contentStyle={{ paddingBottom: 0 }}>
          <UpdateSummaryField
            value={updateSummary}
            onChange={setUpdateSummary}
            testId="changes-step-summary"
          />
        </FooterDockRow>
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            color="primary"
            data-testid="share-wizard-next"
            disabled={!canAdvance}
            onClick={nav.goForward}
            style={{ margin: 0 }}
          >
            Next
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>
    </IonPage>
  );
};

export default ChangesStep;
